import { useEffect, useMemo, useState } from 'react';
import { Tooltip, Typography } from '@mui/material';
import { DeleteOutline } from '@mui/icons-material';
import { FlowsDataSource } from '../../../../datasources/flows/FlowsDataSource';
import {
  ProductFlowLink,
  ProductFlowsDataSource,
} from '../../../../datasources/productFlows/ProductFlowsDataSource';
import {
  Question,
  QuestionsDataSource,
} from '../../../../datasources/questions/QuestionsDataSource';
import {
  getApiErrorMessage,
  getGenericErrorMessage,
} from '../../../../datasources/GenericErrors';
import { BasketsDataSource } from '../../../../datasources/baskets/BasketsDataSource';
import { Basket } from '../../../../types/basket/Basket';
import { Flow, FlowPayload, FlowType } from '../../../../types/flow/Flow';
import { BasketListScreen } from '../shared/BasketListScreen';
import { BasketTable, BasketTableColumn } from '../shared/BasketTable';
import { useTextSearch } from '../shared/useTextSearch';
import { FLOW_TYPES, validateDefinition } from './flowBuilder.helpers';
import {
  FlowStep,
  definitionToSteps,
  describeStep,
  isStepsValid,
  stepsToDefinition,
  validateSteps,
} from './flowSteps';
import { FlowEditorDialog } from './FlowEditorDialog';

const SEQUENCE_PREVIEW = 2;
const QUESTION_NAME_LIMIT = 28;

type FlowRow = {
  flow: Flow;
  linkedProducts: number;
};

function shortName(text: string): string {
  return text.length > QUESTION_NAME_LIMIT
    ? `${text.slice(0, QUESTION_NAME_LIMIT - 1)}…`
    : text;
}

type FlowPageProps = {
  /** Fluxo destacado na tabela (ex.: vindo do link da aba Empresas). */
  highlightFlowId?: number;
};

export const FlowPage = ({ highlightFlowId }: FlowPageProps) => {
  const [flows, setFlows] = useState<Flow[]>([]);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [baskets, setBaskets] = useState<Basket[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string>();
  const [retry, setRetry] = useState(0);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingFlow, setEditingFlow] = useState<Flow | null>(null);
  const [code, setCode] = useState('');
  const [type, setType] = useState<FlowType>('MAIN');
  const [steps, setSteps] = useState<FlowStep[]>([]);
  const [editorError, setEditorError] = useState<string>();
  const [saving, setSaving] = useState(false);
  const [productLinks, setProductLinks] = useState<ProductFlowLink[]>([]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(undefined);

    Promise.all([
      FlowsDataSource.getAll(),
      QuestionsDataSource.getAll(),
      BasketsDataSource.getAll(),
      // Só alimenta a coluna "Produtos": sem ela a lista continua funcionando.
      ProductFlowsDataSource.getAll().catch(() => [] as ProductFlowLink[]),
    ])
      .then(([flowData, questionData, basketData, linkData]) => {
        if (cancelled) {
          return;
        }
        setFlows(flowData);
        setQuestions(questionData);
        setBaskets(basketData);
        setProductLinks(linkData);
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setLoadError(
            getGenericErrorMessage(
              error,
              'Não foi possível carregar os fluxos e as perguntas disponíveis.'
            )
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [retry]);

  const questionsByCode = useMemo(
    () => new Map(questions.map((question) => [question.code, question])),
    [questions]
  );

  function resetEditor(flow?: Flow): void {
    setEditingFlow(flow ?? null);
    setCode(flow?.code ?? '');
    setType(flow?.type ?? 'MAIN');
    setSteps(definitionToSteps(flow?.definition));
    setEditorError(undefined);
    setEditorOpen(true);
  }

  async function saveFlow(): Promise<void> {
    const normalizedCode = code.trim().toUpperCase();

    if (!normalizedCode) {
      setEditorError('Informe um nome para identificar o fluxo.');
      return;
    }

    const stepsValidation = validateSteps(steps, questionsByCode);
    if (!isStepsValid(stepsValidation)) {
      setEditorError(
        stepsValidation.general ??
          'Revise as perguntas destacadas antes de salvar.'
      );
      return;
    }

    const definition = stepsToDefinition(steps);
    const definitionError = validateDefinition(definition, questionsByCode);
    if (definitionError) {
      setEditorError(definitionError);
      return;
    }

    const duplicateCode = flows.some(
      (flow) =>
        flow.id !== editingFlow?.id &&
        flow.code.trim().toUpperCase() === normalizedCode
    );
    if (duplicateCode) {
      setEditorError('Já existe um fluxo com esse nome.');
      return;
    }

    const payload: FlowPayload = {
      code: normalizedCode,
      type,
      nodes: definition.nodes,
      edges: definition.edges,
    };

    setSaving(true);
    setEditorError(undefined);

    try {
      if (editingFlow) {
        await FlowsDataSource.updateFlow(editingFlow.id, payload);
      } else {
        await FlowsDataSource.createFlow(payload);
      }

      setEditorOpen(false);
      setFlows(await FlowsDataSource.getAll());
    } catch (error) {
      setEditorError(
        getApiErrorMessage(error, 'Não foi possível salvar o fluxo.')
      );
    } finally {
      setSaving(false);
    }
  }

  async function deleteFlow(flow: Flow): Promise<void> {
    if (!window.confirm(`Deseja realmente excluir o fluxo ${flow.code}?`)) {
      return;
    }
    try {
      await FlowsDataSource.deleteFlow(flow.id);
      setFlows((currentFlows) =>
        currentFlows.filter((item) => item.id !== flow.id)
      );
      setLoadError(undefined);
    } catch (error) {
      setLoadError(
        getApiErrorMessage(error, 'Não foi possível excluir o fluxo.')
      );
    }
  }

  const rows: FlowRow[] = useMemo(
    () =>
      flows.map((flow) => ({
        flow,
        linkedProducts: productLinks.filter(
          (link) => link.basket_flow_id === flow.id
        ).length,
      })),
    [flows, productLinks]
  );

  const columns: BasketTableColumn<FlowRow>[] = [
    {
      id: 'flow',
      label: 'Fluxo',
      minWidth: 220,
      render: ({ flow }) => (
        <Typography variant="body2" fontWeight={700}>
          {flow.code}
        </Typography>
      ),
    },
    {
      id: 'type',
      label: 'Tipo',
      minWidth: 120,
      render: ({ flow }) =>
        FLOW_TYPES.find((item) => item.value === flow.type)?.label ??
        'Tipo não identificado',
    },
    {
      id: 'questions',
      label: 'Perguntas',
      minWidth: 100,
      render: ({ flow }) => flow.definition?.nodes?.length ?? 0,
    },
    {
      id: 'sequence',
      label: 'Sequência',
      minWidth: 300,
      render: ({ flow }) => {
        const steps = definitionToSteps(flow.definition);
        if (steps.length === 0) return '—';

        const nameOf = (step: FlowStep) =>
          shortName(questionsByCode.get(step.questionCode)?.text ?? step.questionCode);
        const preview = steps.slice(0, SEQUENCE_PREVIEW).map(nameOf).join(' → ');
        const extra = steps.length - SEQUENCE_PREVIEW;

        return (
          <Tooltip
            title={
              <>
                {steps.map((step, index) => (
                  <div
                    key={`${step.questionCode}-${index}`}
                    style={{ marginBottom: 6 }}
                  >
                    <strong>
                      {index + 1}.{' '}
                      {questionsByCode.get(step.questionCode)?.text ?? step.questionCode}
                    </strong>
                    <div>{describeStep(step, questionsByCode)}</div>
                  </div>
                ))}
              </>
            }
          >
            <span>{extra > 0 ? `${preview} → +${extra}` : preview}</span>
          </Tooltip>
        );
      },
    },
    {
      id: 'products',
      label: 'Produtos',
      minWidth: 90,
      render: ({ linkedProducts }) => linkedProducts,
    },
  ];

  return (
    <>
      <BasketListScreen
        title="Fluxos"
        description="Monte a sequência de perguntas de cada atendimento."
        actionLabel="Novo fluxo"
        onAction={() => resetEditor()}
        searchPlaceholder="Buscar por fluxo ou pergunta..."
        loading={loading}
        loadingLabel="Carregando fluxos"
        error={loadError}
        onRetry={() => setRetry((attempt) => attempt + 1)}
      >
        {(search) => (
          <FlowsTable
            rows={rows}
            search={search}
            columns={columns}
            questionsByCode={questionsByCode}
            highlightFlowId={highlightFlowId}
            onEdit={(row) => resetEditor(row.flow)}
            onDelete={(row) => void deleteFlow(row.flow)}
          />
        )}
      </BasketListScreen>

      <FlowEditorDialog
        editorOpen={editorOpen}
        onClose={() => setEditorOpen(false)}
        saving={saving}
        editorError={editorError}
        editingFlow={editingFlow}
        code={code}
        setCode={setCode}
        type={type}
        setType={setType}
        steps={steps}
        setSteps={setSteps}
        questions={questions}
        questionsByCode={questionsByCode}
        baskets={baskets}
        saveFlow={() => void saveFlow()}
      />
    </>
  );
};

type FlowsTableProps = {
  rows: FlowRow[];
  search: string;
  columns: BasketTableColumn<FlowRow>[];
  questionsByCode: Map<string, Question>;
  highlightFlowId?: number;
  onEdit: (row: FlowRow) => void;
  onDelete: (row: FlowRow) => void;
};

const FlowsTable = ({
  rows,
  search,
  columns,
  questionsByCode,
  highlightFlowId,
  onEdit,
  onDelete,
}: FlowsTableProps) => {
  const filtered = useTextSearch(rows, search, ({ flow }) => [
    flow.code,
    ...(flow.definition?.nodes ?? []).map(
      (node) => questionsByCode.get(node.question)?.text ?? node.question
    ),
  ]);

  return (
    <BasketTable
      columns={columns}
      rows={filtered}
      getRowId={({ flow }) => flow.id}
      getRowLabel={({ flow }) => `fluxo ${flow.code}`}
      itemsLabel="fluxos"
      emptyMessage="Nenhum fluxo cadastrado."
      searching={search.trim() !== ''}
      resetKey={search}
      highlightRowId={highlightFlowId}
      onEdit={onEdit}
      editLabel="Editar"
      menuActions={[
        {
          label: 'Excluir',
          icon: <DeleteOutline fontSize="small" />,
          destructive: true,
          onClick: onDelete,
        },
      ]}
    />
  );
};
